import { clearSortUrl, updateSortAscendingUrl, updateSortDescendingUrl } from "./url-table-menu-sort-options.utilities";
import { decamelize } from "humps";
import { FunctionComponent, memo, useCallback } from "react";
import { IProps } from "./url-table-menu-sort-options.types";
import { isOrderedByAscending } from "utilities/is-ordered-by-ascending";
import { isOrderedByProperty } from "utilities/is-ordered-by-property";
import { TableMenuSortOptions, useQueryParameters } from "@edgetier/client-components";
import { useHistory, useLocation } from "react-router-dom";
import "./url-table-menu-sort-options.scss";

/**
 * Column sorting options that will update the URL.
 * @param props.property             Sorting property that will be sent to the backend.
 * @param props.queryParameterPrefix Optional string to prefix any parameter names.
 * @param props.reverseDirection     Make descending sort ascending and vice versa.
 * @returns                          Menu with sorting options.
 */
const UrlTableMenuSortOptions: FunctionComponent<IProps> = ({
    property,
    queryParameterPrefix,
    reverseDirection = false,
}) => {
    const { push } = useHistory();
    const { search } = useLocation();

    // Get the current sort settings from the URL.
    const { orderBy } = useQueryParameters({ orderBy: String }, { prefix: queryParameterPrefix });

    const isSortedAscending = isOrderedByAscending(decamelize(property), orderBy);

    /**
     * Remove any sorting.
     */
    const onClearSort = useCallback(() => {
        push(clearSortUrl(queryParameterPrefix, search));
    }, [push, queryParameterPrefix, search]);

    /**
     * Sort ascending.
     */
    const onSortAscending = useCallback(() => {
        push(updateSortAscendingUrl(queryParameterPrefix, property, search));
    }, [property, push, queryParameterPrefix, search]);

    /**
     * Sort descending.
     */
    const onSortDescending = useCallback(() => {
        push(updateSortDescendingUrl(queryParameterPrefix, property, search));
    }, [property, push, queryParameterPrefix, search]);

    return (
        <TableMenuSortOptions
            isSorted={isOrderedByProperty(decamelize(property), orderBy)}
            isSortedAscending={reverseDirection ? !isSortedAscending : isSortedAscending}
            onClearSort={onClearSort}
            onSortAscending={reverseDirection ? onSortDescending : onSortAscending}
            onSortDescending={reverseDirection ? onSortAscending : onSortDescending}
        />
    );
};

export default memo(UrlTableMenuSortOptions);
